import { useSession } from "hooks/useSession";
import React, { useState } from "react";

const UPI_BLUE = "#002970";
const QUICK_AMOUNTS = [100, 500, 1000, 2000];
const UPI_CHIPS = [
  { label: "GPay",    color: "#4CAF50" },
  { label: "PhonePe", color: "#673AB7" },
  { label: "BHIM",    color: UPI_BLUE  },
  { label: "& more",  color: "#9e9e9e" },
];

function formatINR(n) {
  return n.toLocaleString("en-IN");
}

// ── Toast ─────────────────────────────────────────────────────────────────────
function Toast({ message, color, visible }) {
  return (
    <div style={{
      position: "fixed", bottom: 32, left: "50%",
      transform: `translateX(-50%) translateY(${visible ? 0 : 20}px)`,
      opacity: visible ? 1 : 0,
      transition: "all 0.3s ease",
      background: color, color: "#fff",
      padding: "10px 20px", borderRadius: 8,
      fontSize: 13, fontWeight: 500,
      zIndex: 9999, pointerEvents: "none",
      boxShadow: "0 4px 16px rgba(0,0,0,.2)",
      whiteSpace: "nowrap",
    }}>
      {message}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function LoadWalletPayThroughUpi({ show, onClose }) {
  const { userid } = useSession();

  const [amount,  setAmount]  = useState("");
  const [error,   setError]   = useState("");
  const [loading, setLoading] = useState(false);
  const [toast,   setToast]   = useState({ visible: false, message: "", color: "#333" });

  // Reset on close
  useState_reset(show, () => {
    setAmount(""); setError(""); setLoading(false);
  });

  // ── Toast helper ──
  function showToast(message, color) {
    setToast({ visible: true, message, color });
    setTimeout(() => setToast(t => ({ ...t, visible: false })), 3500);
  }

  // ── Validate amount ──
  function validate() {
    if (!amount)              { setError("Enter an amount");       return false; }
    const n = parseInt(amount, 10);
    if (isNaN(n) || n < 10)   { setError("Minimum amount is ₹10"); return false; }
    if (n > 50000)            { setError("Maximum is ₹50,000");    return false; }
    setError("");
    return true;
  }

  // ── Step 1: create order & get payment URL, then redirect the whole tab ──
  async function rechargeNow() {
    if (!validate()) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("amount",  amount);
      fd.append("userid",  userid);
      fd.append("remark",  "load wallet");

      const res  = await fetch("https://pro.mokshiq.in/create_order_wallet_topup", { method: "POST", body: fd });
      const json = await res.json();

      if (json.status === true) {
        const data = json.data ?? {};
        const pUrl = data.payment_url ?? "";
        if (!pUrl) throw new Error("Missing payment URL");

        // Navigate the current tab to the payment gateway instead of
        // opening an iframe. The gateway's return URL is expected to
        // bring the user back into the app (e.g. to a /wallet/status
        // route) which should call the status-check endpoint on load.
        window.location.href = pUrl;
      } else {
        showToast(json.message ?? "Something went wrong", "#d32f2f");
      }
    } catch {
      showToast("An error occurred. Please try again.", "#d32f2f");
    } finally {
      setLoading(false);
    }
  }

  if (!show) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={() => { if (!loading) onClose(); }}
        style={{
          position: "fixed", inset: 0,
          background: "rgba(0,0,0,.45)",
          zIndex: 1040, backdropFilter: "blur(2px)",
        }}
      />

      {/* Modal panel */}
      <div style={{
        position: "fixed", top: "50%", left: "50%",
        transform: "translate(-50%,-50%)",
        width: "min(520px, 96vw)",
        maxHeight: "90vh",
        display: "flex", flexDirection: "column",
        background: "#F4F6F9", borderRadius: 18,
        overflow: "hidden",
        boxShadow: "0 24px 64px rgba(0,41,112,.25)",
        zIndex: 1050,
        animation: "slideUp .28s cubic-bezier(.22,1,.36,1)",
      }}>

        {/* AppBar */}
        <div style={{
          background: UPI_BLUE, color: "#fff",
          padding: "14px 16px",
          display: "flex", alignItems: "center", gap: 12,
          flexShrink: 0,
        }}>
          <button onClick={onClose} style={iconBtnStyle} aria-label="Back">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>

          <span style={{ flex: 1, fontWeight: 600, fontSize: 16, letterSpacing: .1 }}>
            Add money via UPI
          </span>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflow: "auto", display: "flex", flexDirection: "column" }}>
          <FormView
            amount={amount}
            setAmount={v => { setAmount(v); setError(""); }}
            error={error}
            loading={loading}
            onSubmit={rechargeNow}
          />
        </div>
      </div>

      <Toast {...toast} />

      <style>{`
        @keyframes slideUp {
          from { opacity:0; transform:translate(-50%, calc(-50% + 24px)); }
          to   { opacity:1; transform:translate(-50%, -50%); }
        }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </>
  );
}

// small helper to reset form state when the modal closes
function useState_reset(show, resetFn) {
  const prevShow = React.useRef(show);
  React.useEffect(() => {
    if (prevShow.current && !show) resetFn();
    prevShow.current = show;
  }, [show]);
}

// ── FormView ──────────────────────────────────────────────────────────────────
function FormView({ amount, setAmount, error, loading, onSubmit }) {
  return (
    <div style={{ padding:16, display:"flex", flexDirection:"column", gap:14 }}>

      {/* UPI chips */}
      <div style={{ display:"flex", gap:8, overflowX:"auto", paddingBottom:2 }}>
        {UPI_CHIPS.map(({ label, color }) => (
          <div key={label} style={chipStyle}>
            <span style={{ width:8, height:8, borderRadius:"50%", background:color, flexShrink:0 }} />
            <span style={{ fontSize:12, color:"#666", whiteSpace:"nowrap" }}>{label}</span>
          </div>
        ))}
      </div>

      {/* Amount card */}
      <div style={cardStyle}>
        <div style={{ fontSize:10, fontWeight:700, color:"#9e9e9e", letterSpacing:1 }}>ENTER AMOUNT</div>
        <div style={{ display:"flex", alignItems:"center", gap:8, marginTop:8 }}>
          <span style={{ fontSize:28, fontWeight:700, color:UPI_BLUE }}>₹</span>
          <input
            type="number"
            value={amount}
            onChange={e => setAmount(e.target.value.replace(/\D/g,""))}
            placeholder="0"
            min={10} max={50000}
            style={{
              flex:1, fontSize:28, fontWeight:500,
              border:"none", outline:"none",
              background:"transparent", color:"#111", width:"100%",
            }}
          />
        </div>
        <div style={{ height:2, background: error ? "#d32f2f" : UPI_BLUE, borderRadius:2, marginTop:4 }} />
        {error && <div style={{ color:"#d32f2f", fontSize:11, marginTop:4 }}>{error}</div>}

        <div style={{ display:"flex", flexWrap:"wrap", gap:8, marginTop:14 }}>
          {QUICK_AMOUNTS.map(amt => (
            <button key={amt} onClick={() => setAmount(String(amt))} style={quickAmtStyle(String(amt) === amount)}>
              + ₹{formatINR(amt)}
            </button>
          ))}
        </div>
      </div>

      {/* Info banner */}
      <div style={infoBannerStyle}>
        <svg width="16" height="16" viewBox="0 0 24 24" style={{ flexShrink:0, marginTop:1 }}>
          <circle cx="12" cy="12" r="10" fill={UPI_BLUE} />
          <line x1="12" y1="8"  x2="12"    y2="12"   stroke="white" strokeWidth="2" strokeLinecap="round"/>
          <line x1="12" y1="16" x2="12.01" y2="16"   stroke="white" strokeWidth="2" strokeLinecap="round"/>
        </svg>
        <span style={{ fontSize:11, color:"#0C447C", lineHeight:1.6 }}>
          Money will be added to your Mokshiq wallet instantly. Min ₹10 · Max ₹50,000 per transaction.
        </span>
      </div>

      {/* Pay button */}
      {loading ? (
        <div style={{ display:"flex", justifyContent:"center", padding:"10px 0" }}>
          <div style={{ width:28, height:28, border:`3px solid ${UPI_BLUE}`, borderTopColor:"transparent", borderRadius:"50%", animation:"spin .8s linear infinite" }} />
        </div>
      ) : (
        <button onClick={onSubmit} style={payBtnStyle}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight:8 }}>
            <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
          </svg>
          Proceed to pay ₹{amount ? formatINR(parseInt(amount,10)) : "0"}
        </button>
      )}

      {/* Secure badge */}
      <div style={{ display:"flex", justifyContent:"center", alignItems:"center", gap:5, paddingBottom:4 }}>
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#9e9e9e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
          <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
        </svg>
        <span style={{ fontSize:10, color:"#9e9e9e" }}>100% secure · encrypted · RBI compliant</span>
      </div>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────
const iconBtnStyle = {
  background:"none", border:"none", color:"#fff",
  cursor:"pointer", display:"flex", alignItems:"center",
  padding:4, borderRadius:6,
};
const chipStyle = {
  display:"flex", alignItems:"center", gap:6,
  padding:"6px 12px",
  background:"#fff", border:"1px solid #e0e0e0", borderRadius:20,
  flexShrink:0,
};
const cardStyle = {
  background:"#fff", borderRadius:16,
  padding:20, border:"1px solid #ececec",
};
const infoBannerStyle = {
  background:"#E3F7FF", borderRadius:10,
  padding:"10px 12px",
  display:"flex", gap:8, alignItems:"flex-start",
};
const payBtnStyle = {
  width:"100%", background:UPI_BLUE, color:"#fff",
  border:"none", borderRadius:14,
  padding:"14px 0", fontSize:15, fontWeight:600,
  cursor:"pointer", display:"flex", alignItems:"center",
  justifyContent:"center", letterSpacing:.2,
  transition:"opacity .15s",
};
function quickAmtStyle(active) {
  return {
    background: active ? "#b3e8f9" : "#E3F7FF",
    border: `1px solid ${active ? "#0C447C" : "#B3E8F9"}`,
    borderRadius:20, padding:"6px 14px",
    fontSize:12, fontWeight:600, color:"#0C447C",
    cursor:"pointer", transition:"all .15s",
  };
}